// ============================================================================
// Supply Management — landing page.
// Shows the current user's supply requests grouped by lifecycle bucket,
// with a fixed bottom-right voice button to start a new request. Mobile-
// first layout: cards stretch full-width on phones, two-up on desktop.
//
// Scoping:
//   * superuser     → see all requests (org-wide)
//   * account_manager → see all requests (so AMs can cover for each other);
//                       defaults to showing "Mine" first via the toggle
//   * other roles    → empty list (RLS won't return rows anyway, the toggle
//                      is hidden)
// ============================================================================

import { seesSupplyScopeSwitch } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import { RAG_COLORS } from '../../shared/design-tokens';
import { ErrorState, PageHeader } from '../../shared/components';
import type {
  SupplyRequest,
  SupplyRequestStatus,
  SupplyUrgency,
} from '../../shared/types';
import NewSupplyRequest from './new-request';
import { SupplySubNav } from './supply-sub-nav';
import { errorMessage } from '../../shared/errors';

interface RequestRow extends SupplyRequest {
  facility_name: string | null;
  supply_name: string | null;
  supply_category: string | null;
}

// Bucket the 8-state lifecycle into UI-friendly columns. Keep these in sync
// with the supply_requests_status_check CHECK constraint.
// Reconciled 2026-05-12. See PROJECTS/tcs-erp-database/artifacts/code-and-db-audit-2026-05-12.md.
const STATUS_BUCKETS: { label: string; statuses: SupplyRequestStatus[] }[] = [
  {
    label: 'Open',
    statuses: ['Submitted', 'Approved'],
  },
  {
    label: 'Fulfilling',
    statuses: ['Ordered', 'Shipped'],
  },
  {
    label: 'Closed',
    statuses: ['Delivered', 'Fulfilled', 'Rejected', 'Cancelled'],
  },
];

export default function SupplyRequestsPage() {
  const { employee, profile } = useAuth();
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');

  const role = profile?.role;
  const showScopeToggle = seesSupplyScopeSwitch(role);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Flat select + parallel IN-lookups, joined client-side. This pattern
      // doesn't depend on PostgREST resource embedding, so it's resilient to
      // missing-FK / stale-schema-cache classes of bug (see
      // 20260516_schema_reconcile.sql and docs/database-changes.md).
      let q = supabase
        .from('supply_requests')
        .select(
          `request_id, facility_id, account_id, supply_id, item_description,
           quantity, unit_of_measure, urgency, date_needed, requested_by,
           approved_by, approved_at, ship_to_address, reimbursable, notes,
           status, estimated_cost, vendor_selected, created_at, updated_at`,
        );
      if (scope === 'mine' && employee?.employee_id) {
        q = q.eq('requested_by', employee.employee_id);
      }
      const { data: flat, error: e } = await q.order('created_at', {
        ascending: false,
      });
      if (e) throw e;
      const flatRows = (flat ?? []) as unknown as SupplyRequest[];

      const facIds = Array.from(new Set(flatRows.map((r) => r.facility_id).filter(Boolean))) as string[];
      const supIds = Array.from(new Set(flatRows.map((r) => r.supply_id).filter(Boolean))) as string[];
      const [facRes, supRes] = await Promise.all([
        facIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase.from('facilities').select('facility_id, facility_name').in('facility_id', facIds),
        supIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase.from('supplies').select('supply_id, item_name, supply_category').in('supply_id', supIds),
      ]);
      const facMap: Record<string, string | null> = {};
      for (const f of (facRes.data ?? []) as Array<{ facility_id: string; facility_name: string | null }>) {
        facMap[f.facility_id] = f.facility_name;
      }
      const supMap: Record<string, { item_name: string | null; supply_category: string | null }> = {};
      for (const s of (supRes.data ?? []) as Array<{ supply_id: string; item_name: string | null; supply_category: string | null }>) {
        supMap[s.supply_id] = { item_name: s.item_name, supply_category: s.supply_category };
      }

      const enriched: RequestRow[] = flatRows.map((r) => ({
        ...r,
        facility_name: r.facility_id ? facMap[r.facility_id] ?? null : null,
        supply_name: r.supply_id ? supMap[r.supply_id]?.item_name ?? null : null,
        supply_category: r.supply_id ? supMap[r.supply_id]?.supply_category ?? null : null,
      }));
      setRows(enriched);
    } catch (e) {
      console.error('[supply-requests] load failed', e);
      setError(errorMessage(e, 'Failed to load requests.'));
    } finally {
      setLoading(false);
    }
  }, [employee?.employee_id, scope]);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = useMemo(() => {
    return STATUS_BUCKETS.map((b) => ({
      ...b,
      rows: rows.filter((r) =>
        b.statuses.includes(r.status as SupplyRequestStatus),
      ),
    }));
  }, [rows]);

  const openCount =
    buckets.find((b) => b.label === 'Open')?.rows.length ?? 0;

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <SupplySubNav />
      <PageHeader
        title="Supply requests"
        meta={
          loading
            ? 'Loading…'
            : `${rows.length} request${rows.length === 1 ? '' : 's'} · ${openCount} open`
        }
        actions={
          showScopeToggle && (
            <div className="flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-sm">
              <button
                className={`rounded px-3 py-1 ${scope === 'mine' ? 'bg-tcs-surface font-medium' : 'text-tcs-text-secondary'}`}
                onClick={() => setScope('mine')}
              >
                Mine
              </button>
              <button
                className={`rounded px-3 py-1 ${scope === 'all' ? 'bg-tcs-surface font-medium' : 'text-tcs-text-secondary'}`}
                onClick={() => setScope('all')}
              >
                All
              </button>
            </div>
          )
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorState body={error} onRetry={load} />
        </div>
      )}

      {/* Buckets */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {buckets.map((b) => (
          <section key={b.label}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              {b.label} · {b.rows.length}
            </h2>
            {b.rows.length === 0 ? (
              <p className="rounded-md border border-dashed border-tcs-border-strong bg-tcs-raised p-4 text-center text-xs text-tcs-text-muted">
                Nothing here.
              </p>
            ) : (
              <ul className="space-y-2">
                {b.rows.map((r) => (
                  <RequestCard key={r.request_id} row={r} />
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {!loading && rows.length === 0 && (
        <div className="mt-6 rounded-lg border border-dashed border-tcs-border-strong bg-tcs-raised p-8 text-center">
          <p className="text-sm text-tcs-text-secondary">
            No supply requests yet. Tap the mic to make your first request.
          </p>
        </div>
      )}

      {/* Floating new-request button — fixed bottom-right per the spec.
          Uses tcs-text (slate-900) primary color matching the rest of the
          app's primary buttons. */}
      <button
        type="button"
        onClick={() => setShowNew(true)}
        aria-label="New supply request"
        className="fixed bottom-6 right-6 flex h-16 w-16 items-center justify-center rounded-full bg-tcs-inverse text-tcs-inverse-ink shadow-lg transition-colors hover:bg-tcs-inverse-hover active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent focus-visible:ring-offset-2"
      >
        <Plus className="h-7 w-7" strokeWidth={2.5} />
      </button>

      {showNew && (
        <NewSupplyRequest
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card — individual request row in the bucket lists.
// ---------------------------------------------------------------------------

function RequestCard({ row }: { row: RequestRow }) {
  const urgency = row.urgency as SupplyUrgency;
  const dotColor =
    urgency === 'Emergency'
      ? RAG_COLORS.Red
      : urgency === 'Soon'
        ? RAG_COLORS.Amber
        : RAG_COLORS.Green;
  const item = row.supply_name?.trim() || row.item_description;
  return (
    <li className="rounded-lg border border-tcs-border bg-tcs-raised p-3 shadow-sm">
      <div className="flex items-start gap-2">
        <span
          className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: dotColor }}
          aria-label={`Urgency ${urgency}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="truncate text-sm font-medium text-tcs-text">
              {item}
            </h3>
            <span className="shrink-0 text-xs text-tcs-text-muted">
              {row.quantity} {row.unit_of_measure}
            </span>
          </div>
          <div className="mt-0.5 truncate text-xs text-tcs-text-muted">
            {row.facility_name ?? row.facility_id ?? '—'} · {urgency}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-tcs-text-muted">
            <span>{timeAgo(row.created_at)}</span>
            <StatusPill status={row.status as SupplyRequestStatus} />
          </div>
        </div>
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: SupplyRequestStatus }) {
  const styles: Partial<Record<SupplyRequestStatus, string>> = {
    Submitted: 'bg-tcs-surface text-tcs-text-secondary',
    Approved: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
    Rejected: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
    Ordered: 'bg-st-info-bg-strong text-st-info-fg-strong',
    Shipped: 'bg-indigo-100 text-indigo-800',
    Delivered: 'bg-teal-100 text-teal-800',
    Fulfilled: 'bg-tcs-surface-strong text-tcs-text-secondary',
    Cancelled: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        styles[status] ?? 'bg-tcs-surface text-tcs-text-secondary'
      }`}
    >
      {status}
    </span>
  );
}

// Lightweight, dependency-free relative time.
function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diffMs < min) return 'just now';
  if (diffMs < hr) return `${Math.round(diffMs / min)}m ago`;
  if (diffMs < day) return `${Math.round(diffMs / hr)}h ago`;
  return `${Math.round(diffMs / day)}d ago`;
}
